import React, { useState } from 'react';
import { Container, Paper, Box, TextField, Button, Typography, Alert, CircularProgress } from '@mui/material';
import { useNavigate, Link } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmailIcon from '@mui/icons-material/Email';
import SelectivateLogo from '../components/SelectivateLogo';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const response = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (data.success) {
        setMessage(data.message);
        setEmailSent(true);
      } else {
        setError(data.message || 'An error occurred. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Forgot password error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 'calc(100vh - 80px)',
        }}
      >
        <Box
          sx={{ 
            mb: 4, 
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center', 
          }}
        >
          <Box 
            component="img" 
            src="/SELECCIÓN_logo_PNG.png"
            alt="SELECCIÓN Consulting"
            sx={{ 
              width: 200, 
              mb: 2
            }}
          />
          <Box sx={{ mb: 2 }}>
            <SelectivateLogo variant="banner" size="large" />
          </Box>
          <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 2 }}>
            Reset your password
          </Typography>
        </Box>
        
        <Paper 
          elevation={4} 
          sx={{ 
            p: 4, 
            maxWidth: 450, 
            width: '100%',
            borderRadius: '16px'
          }}
        >
          {!emailSent ? (
            <Box component="form" onSubmit={handleSubmit}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <EmailIcon sx={{ color: '#4CAF50', mr: 1 }} />
                <Typography variant="h5" color="#4CAF50" fontWeight="600">
                  Forgot Password
                </Typography>
              </Box>
              
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Enter your email address and we'll send you a link to reset your password.
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <TextField
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                margin="normal"
                required
                autoFocus
                autoComplete="email"
                sx={{ mb: 2 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading || !email}
                sx={{ 
                  mt: 2, 
                  mb: 2,
                  height: 48,
                  backgroundColor: '#4CAF50',
                  '&:hover': {
                    backgroundColor: '#45a049',
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Send Reset Link'
                )}
              </Button>

              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Button
                  component={Link}
                  to="/login"
                  startIcon={<ArrowBackIcon />}
                  sx={{ 
                    color: '#4CAF50',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: 'rgba(76, 175, 80, 0.04)',
                    },
                  }}
                >
                  Back to Login
                </Button>
              </Box>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                <EmailIcon sx={{ color: '#4CAF50', mr: 1, fontSize: 32 }} />
                <Typography variant="h5" color="#4CAF50" fontWeight="600">
                  Check Your Email
                </Typography>
              </Box>
              
              <Alert severity="success" sx={{ mb: 3 }}>
                {message}
              </Alert>
              
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                If an account with that email exists, you should receive the password reset link within a few minutes.
              </Typography>
              
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Didn't receive the email? Check your spam folder or try again with a different email address.
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setEmailSent(false);
                    setEmail('');
                    setMessage('');
                    setError('');
                  }}
                  sx={{ 
                    borderColor: '#4CAF50',
                    color: '#4CAF50',
                    '&:hover': {
                      borderColor: '#45a049',
                      backgroundColor: 'rgba(76, 175, 80, 0.04)',
                    },
                  }}
                >
                  Try Again
                </Button>
                
                <Button
                  component={Link}
                  to="/login"
                  variant="contained"
                  sx={{ 
                    backgroundColor: '#4CAF50',
                    '&:hover': {
                      backgroundColor: '#45a049',
                    },
                  }}
                >
                  Back to Login
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default ForgotPasswordPage;